import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { MAIN_REGISTRATION_FORM, EVENT } from "@/data/eventData";
import { scrollToTarget } from "@/lib/scroll";

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-[0.08em]">
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden"
        >
            {/* cinematic backdrop — official I-ACE conference art */}
            <motion.div aria-hidden="true" style={{ y: bgY }} className="absolute inset-0">
                <img
                    src="/images/hero-hall.jpg"
                    alt=""
                    className="h-[115%] w-full object-cover object-[62%_28%]"
                />
                <div className="absolute inset-0 bg-maroon/30 mix-blend-multiply" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_60%_at_50%_-10%,rgba(201,31,38,0.22),transparent_62%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_45%_at_50%_115%,rgba(36,6,8,0.9),transparent_70%)]" />
            </motion.div>

            {/* red circular arcs */}
            <div aria-hidden="true" className="absolute -right-32 top-1/2 hidden -translate-y-1/2 md:block lg:right-[-80px]">
                <svg width="560" height="560" viewBox="0 0 560 560" fill="none">
                    <circle cx="280" cy="280" r="278" stroke="#C91F26" strokeOpacity="0.22" />
                    <circle cx="280" cy="280" r="214" stroke="#C91F26" strokeOpacity="0.15" />
                    <circle cx="280" cy="280" r="150" stroke="#C91F26" strokeOpacity="0.1" />
                    <circle
                        cx="280"
                        cy="280"
                        r="246"
                        stroke="#E3262E"
                        strokeOpacity="0.35"
                        strokeDasharray="2 16"
                        className="animate-spin-slow"
                        style={{ transformOrigin: "280px 280px" }}
                    />
                </svg>
            </div>

            {/* readability gradients */}
            <div aria-hidden="true" className="absolute inset-0 bg-ink/55 md:hidden" />
            <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(to_top,#070708_2%,transparent_35%)]" />
            <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(to_right,rgba(7,7,8,0.7),transparent_55%)]" />

            <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pt-24 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-center gap-4"
                >
                    <span className="h-px w-12 bg-scarlet" aria-hidden="true" />
                    <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.45em] text-scarlet md:text-xs">
                        I-ACE Chapter 4
                    </p>
                </motion.div>

                <h1
                    data-testid="hero-heading"
                    className="mt-8 font-serif text-[15vw] font-light leading-[0.95] tracking-tight text-cream sm:text-[11vw] lg:text-[7.8vw]"
                >
                    <MaskedLine delay={0.3}>Debate today,</MaskedLine>
                    <MaskedLine delay={0.45}>
                        <em className="not-italic text-scarlet">
                            <span className="italic">Shape</span>
                        </em>{" "}
                        tomorrow.
                    </MaskedLine>
                </h1>

                <motion.p
                    data-testid="hero-description"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-8 max-w-xl text-base font-light leading-relaxed text-ash md:text-lg"
                >
                    I-ACE 4.0 brings Model United Nations and Indian Youth Parliament
                    together for two days of diplomacy, democracy, and consequential
                    ideas at Banaras Hindu University.
                </motion.p>

                <motion.p
                    data-testid="hero-dates"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.9, delay: 1.05 }}
                    className="mt-6 font-sans text-[11px] font-semibold uppercase tracking-[0.35em] text-cream/70"
                >
                    {EVENT.dates} · BHU, Varanasi
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
                >
                    <a
                        href={MAIN_REGISTRATION_FORM}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-register-btn"
                        className="group inline-flex w-full items-center justify-center gap-3 bg-crimson px-9 py-4 font-sans text-xs font-semibold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:bg-scarlet sm:w-auto"
                    >
                        Secure Your Seat
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                    </a>
                    <button
                        type="button"
                        onClick={() => scrollToTarget("#about")}
                        data-testid="hero-explore-btn"
                        className="group inline-flex w-full items-center justify-center gap-3 border border-cream/15 px-9 py-4 font-sans text-xs font-semibold uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:border-crimson hover:bg-maroon/50 sm:w-auto"
                    >
                        Explore I-ACE
                        <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
                    </button>
                </motion.div>
            </motion.div>

            <motion.div
                aria-hidden="true"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4, duration: 1 }}
                className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex"
            >
                <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-ash/60">
                    Scroll
                </span>
                <span className="h-10 w-px bg-gradient-to-b from-crimson to-transparent" />
            </motion.div>
        </section>
    );
};

export default Hero;
